import { useMemo, useState, Dispatch, SetStateAction } from 'react';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { RoundingRule } from 'repository/timekeeper/@types/RoundingRules';
import { RoundingDialog } from 'components/dialog/rouding_rule/RoundingDialog';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
}

interface ContextMenuProps {
    contextMenu: ContextMenuPosition | null;
    setContextMenu: Dispatch<SetStateAction<ContextMenuPosition | null>>;
    selectedRow: RoundingRule[];
    reloading: () => void;
    rows: RoundingRule[];
}

interface MenuOption {
    name: string;
    icon: any;
    click: () => void;
    color: string;
    divider: boolean;
    disabled: boolean;
    selected: boolean;
    onlyOne: boolean;
    permision: boolean;
}

const ContextMenu = ({ contextMenu, setContextMenu, selectedRow, reloading, rows }: ContextMenuProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [localOpen, setLocalOpen] = useState<boolean>(false);
    const [localMode, setLocalMode] = useState<'add' | 'edit' | 'delete' | 'info'>('add');
    const basePath = buildSettingsBasePath('rounding_rules');

    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'rounding_rules'), [location.pathname]);
    const routeOpen = modalRoute.action === 'add' || (modalRoute.action === 'update' && modalRoute.id !== null) || (modalRoute.action === 'detail' && modalRoute.id !== null);
    const routeMode: 'add' | 'edit' | 'info' = modalRoute.action === 'add' ? 'add' : modalRoute.action === 'detail' ? 'info' : 'edit';
    const open = routeOpen || localOpen;
    const mode = routeOpen ? routeMode : localMode;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForDialog = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeDialog = () => {
        if (routeOpen) {
            navigate(basePath);
            return;
        }
        setLocalOpen(false);
    };

    const options: MenuOption[] = [
        {
            name: t('settings.rounding_rule.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('rounding_rules', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            permision: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.RoundingRules') ? true : false,
        },
        {
            name: t('settings.rounding_rule.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('rounding_rules', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selectedRow.length === 1 ? false : true,
            selected: false,
            onlyOne: true,
            permision: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.UpdateRoundingRule') ? true : false,
        },
        {
            name: t('settings.rounding_rule.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setLocalMode('delete');
                setLocalOpen(true);
                setContextMenu(null);
            },
            color: 'error',
            divider: false,
            disabled: true, //selectedRow.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
            permision: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.DeleteRoundingRuleById') ? true : false,
        },
        {
            name: t('settings.rounding_rule.table.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            permision: true,
        },
    ];

    return (
        <>
            <Menu
                keepMounted
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options
                    .filter((option) => option.permision)
                    .map((option, index) => (
                        <div key={index}>
                            {option.divider ? <Divider /> : null}

                            <MenuItem onClick={option.click} disabled={option.disabled}>
                                <ListItemIcon>{option.icon}</ListItemIcon>

                                <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                            </MenuItem>
                        </div>
                    ))}
            </Menu>

            <RoundingDialog open={open} onClose={closeDialog} rows={rows} selectedRow={selectedRowsForDialog} reloading={reloading} mode={mode} />
        </>
    );
};

export { ContextMenu };
